// services/error_handler.ts
import { getCookie } from '../shared/helpers';
import type { AxiosError, AxiosResponse } from 'axios';

interface Observer {
  actions: {
    alert: {
      showError: (message: string) => void;
    };
  };
}

interface ErrorResponse {
  error_code?: string;
  message?: string;
}

let observer: Observer | null = null;

export const setObserver = (newObserver: Observer): void => {
  observer = newObserver;
};

export const ALERT_EXPIRE_SESSION: Record<string, string> = {
  es_ES: 'Su sesión ha caducado',
  en_US: 'Your session has expired',
  pt_BR: 'Sua sessão expirou',
  de_DE: 'Ihre Sitzung ist abgelaufen',
} as const;

const UNAUTHORIZED_REDIRECT_KEY = 'unauthorizedRedirectAt';
const UNAUTHORIZED_REDIRECT_WINDOW_MS = 10000;

const handleUnauthorized = (error: AxiosError<ErrorResponse>): void => {
  const idiom = getCookie('idiom') || 'en_US';
  observer?.actions.alert.showError(ALERT_EXPIRE_SESSION[idiom]);

  // Guard: multiple concurrent 401 responses must not each trigger a separate
  // Auth0 authorize flow. Only the first one within the window redirects.
  const now = Date.now();
  const lastRedirectAt = Number(
    sessionStorage.getItem(UNAUTHORIZED_REDIRECT_KEY)
  );
  const redirectedRecently =
    Number.isFinite(lastRedirectAt) &&
    now - lastRedirectAt < UNAUTHORIZED_REDIRECT_WINDOW_MS;

  if (redirectedRecently) {
    return;
  }
  sessionStorage.setItem(UNAUTHORIZED_REDIRECT_KEY, String(now));

  const errorCode = error.response?.data?.error_code;
  const redirectPath =
    errorCode === 'invalid_refresh_token' ? '/login' : '/logoff';

  window.location.href = redirectPath;
};

const STATUS_HANDLERS: Record<
  number,
  (error: AxiosError<ErrorResponse>) => void
> = {
  401: handleUnauthorized,
};

// Special error message for Singapore consultation restrictions
const SINGAPORE_PHONE_RESTRICTION =
  'Your service request cannot be created. If you are currently located in Singapore, ' +
  'please request a Video Consultation. If it is an emergency, please go to your nearest Emergency Department.';

export interface ErrorHandlerOptions {
  requestType?: string;
  requestData?: Record<string, unknown>;
  countryResidence?: string;
}

export const errorHandler = async <T>(
  request: Promise<AxiosResponse<T>>,
  options: ErrorHandlerOptions = {}
): Promise<AxiosResponse<T> | string> => {
  try {
    return await request;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;

    if (!axiosError.response) {
      observer?.actions.alert.showError('UNEXPECTED_ERROR_OCCURRED');
      throw error;
    }

    const { status, data } = axiosError.response;
    const { requestType, countryResidence } = options;

    // Handle specific status codes
    if (STATUS_HANDLERS[status]) {
      STATUS_HANDLERS[status](axiosError);
      return axiosError.response as unknown as AxiosResponse<T>;
    }

    // Singapore phone consultation restriction
    if (
      (status === 500 || status === 400) &&
      requestType === 'requestPhoneCall' &&
      countryResidence === 'SG' &&
      data?.message?.includes('Service not allowed')
    ) {
      return SINGAPORE_PHONE_RESTRICTION;
    }

    throw error;
  }
};

export type { Observer, ErrorResponse };
